import { useId, type CSSProperties, type ReactNode } from 'react'

/**
 * Marketplace and carrier logos, redrawn in muted pastel tones so they sit quietly
 * beside jewellery photos and warehouse figures. The one place these marks live.
 */

export interface BrandLogoProps {
  size?: number
  style?: CSSProperties
  className?: string
}

// Each rendered logo needs its own clip id; React ids carry characters url() rejects.
function useClipId(prefix: string): string {
  return `${prefix}-${useId().replace(/[^A-Za-z0-9_-]/g, '')}`
}

function LogoSvg({ name, size = 16, style, className, children }: BrandLogoProps & { name: string; children: ReactNode }) {
  return (
    <svg aria-hidden="true" data-brand-logo={name} width={size} height={size} viewBox="0 0 20 20" style={style} className={className}>
      {children}
    </svg>
  )
}

export function YandexMarketLogo(props: BrandLogoProps) {
  const clip = useClipId('ym')
  return (
    <LogoSvg name="yandex-market" {...props}>
      <defs><clipPath id={clip}><circle cx="10" cy="10" r="10" /></clipPath></defs>
      <circle cx="10" cy="10" r="10" fill="#F5D978" />
      <path clipPath={`url(#${clip})`} d="M3.6 15.4 7.9 7.6l1.2 6.6 4.6-7.2 1.5 6.2 4.2-5.6" fill="none" stroke="#3b362d" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
    </LogoSvg>
  )
}

export function YandexDeliveryLogo(props: BrandLogoProps) {
  return (
    <LogoSvg name="yandex-delivery" {...props}>
      <circle cx="10" cy="10" r="10" fill="#EEEAE3" />
      <path d="M4.6 8.2 8 5.4h7.2l-3.4 2.8z" fill="#F3AE95" />
      <path d="M11.8 8.2 15.2 5.4v7.6l-3.4 2.9z" fill="#DE8A6E" />
      <path d="M4.6 8.2h7.2v7.7H4.6z" fill="#EC9A7E" />
      <path d="M6.9 12.2V9.6m-.9.9.9-.9.9.9m1.6 1.7V9.6m-.9.9.9-.9.9.9M6.3 13.6q1.9 1 3.8 0" fill="none" stroke="#fff" strokeWidth={0.8} strokeLinecap="round" strokeLinejoin="round" />
    </LogoSvg>
  )
}

export function WildberriesLogo(props: BrandLogoProps) {
  return (
    <LogoSvg name="wildberries" {...props}>
      <circle cx="9.4" cy="11" r="7.4" fill="#A77FB4" />
      <circle cx="12.2" cy="7.2" r="4.9" fill="#E79BC2" />
      <circle cx="15.6" cy="12.6" r="3.4" fill="#E79BC2" />
      <circle cx="3.9" cy="12.8" r="3" fill="#E79BC2" />
      <circle cx="17.6" cy="3.6" r="1.5" fill="#E79BC2" />
      <path d="M3.4 8.2 4.9 13.6h.9l1.3-4 1.3 4h.9l1.5-5.4" fill="none" stroke="#fff" strokeWidth={1.35} strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12.3 8.2v5.4h2.1a1.45 1.45 0 0 0 0-2.9h-2.1 1.8a1.25 1.25 0 0 0 0-2.5z" fill="none" stroke="#fff" strokeWidth={1.35} strokeLinecap="round" strokeLinejoin="round" />
    </LogoSvg>
  )
}

export function OzonLogo(props: BrandLogoProps) {
  const clip = useClipId('ozon')
  return (
    <LogoSvg name="ozon" {...props}>
      <defs><clipPath id={clip}><rect width="20" height="20" rx="4.5" /></clipPath></defs>
      <g clipPath={`url(#${clip})`}>
        <rect width="20" height="20" fill="#7FA6E6" />
        <path d="M4 20 6.2 12.4 20 11.8V20z" fill="#EB8FA8" />
        <ellipse cx="9.8" cy="9.6" rx="7.4" ry="5" transform="rotate(-18 9.8 9.6)" fill="#fff" />
        <ellipse cx="9.8" cy="9.6" rx="4.4" ry="2.6" transform="rotate(-18 9.8 9.6)" fill="#EB8FA8" />
      </g>
    </LogoSvg>
  )
}

export function CdekLogo(props: BrandLogoProps) {
  return (
    <LogoSvg name="cdek" {...props}>
      <rect width="20" height="20" rx="4.5" fill="#86C79C" />
      <path d="M13.6 6.6a4.6 4.6 0 1 0 0 6.8" fill="none" stroke="#fff" strokeWidth={2.2} strokeLinecap="round" />
    </LogoSvg>
  )
}
